import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Eye, Edit3 } from 'lucide-react';

interface MarkdownEditorProps {
  label: string;
  name: string;
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  example?: string;
  required?: boolean;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  label,
  name,
  value,
  onChange,
  error,
  placeholder,
  example,
  required = false,
}) => {
  const [isPreview, setIsPreview] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[var(--primary-deep-blue)]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setIsPreview(false)}
            className={`p-2 rounded-md transition-colors ${
              !isPreview
                ? 'bg-solid-primary text-white'
                : 'bg-[var(--neutral-light-gray)] text-[var(--secondary-slate)] hover:bg-[var(--neutral-medium-gray)]'
            }`}
          >
            <Edit3 size={16} />
          </button>
          <button
            type="button"
            onClick={() => setIsPreview(true)}
            className={`p-2 rounded-md transition-colors ${
              isPreview
                ? 'bg-solid-primary text-white'
                : 'bg-[var(--neutral-light-gray)] text-[var(--secondary-slate)] hover:bg-[var(--neutral-medium-gray)]'
            }`}
          >
            <Eye size={16} />
          </button>
        </div>
      </div>

      {example && (
        <p className="text-sm text-[var(--secondary-slate)] bg-[var(--secondary-cream)] p-2 rounded-md">
          <strong>Example:</strong> {example}
        </p>
      )}

      <div className="border border-[var(--neutral-medium-gray)] rounded-md overflow-hidden">
        {isPreview ? (
          <div className="p-4 bg-white min-h-[200px] prose prose-sm max-w-none">
            {value ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {value}
              </ReactMarkdown>
            ) : (
              <p className="text-[var(--secondary-slate)] italic">No content to preview</p>
            )}
          </div>
        ) : (
          <textarea
            name={name}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full p-4 border-0 resize-none focus:ring-0 focus:outline-none min-h-[200px]"
            rows={8}
          />
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <div className="text-xs text-[var(--secondary-slate)] bg-[var(--secondary-cream)] p-2 rounded-md">
        <strong>Need help with Markdown?</strong> Check out the{' '}
        <a 
          href="https://www.markdownguide.org/cheat-sheet/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-[var(--primary-emerald)] hover:text-[var(--primary-deep-blue)] underline"
        >
          Markdown Cheat Sheet
        </a>
        {' '}for syntax reference.
      </div>
    </div>
  );
};

export default MarkdownEditor;
